<template>

  <v-container fluid class="mt-0 page page-org-profile">

    <!-- Breadcrumb & Actions -->
    <v-row class="mt-4 mb-5">
      <v-col cols="12" class="d-flex align-center justify-start page-breadcrumb-new">
        <!-- (breadcrumb) -->
        <v-icon small class="mr-2" v-if="uiType === 'sys-admin'">mdi-shield-account</v-icon>
        <v-icon small class="mr-2" v-else>mdi-account-circle-outline</v-icon>
        <div>Org Profile</div>
      </v-col>
    </v-row>

    <template v-if="org">
      <!---------------------->
      <!-- Org Name, Status -->
      <!---------------------->
      <v-container fluid>
        <v-row>
          <v-col cols="12" xs="12" sm="12" md="12" lg="4">
            <div class="page-super-title">{{ orgType }}</div>
            <div class="page-title">{{ org.name }}</div>
            <div class="mt-5" v-if="uiType !== 'sys-admin'">
              <span class="form-field-label">Status:</span>
              <v-chip label outlined :class="`${org.org_status} ml-3`" v-if="org.org_status">
                {{ org.org_status.toUpperCase() }}
              </v-chip>
            </div>
          </v-col>
          <v-col cols="12" xs="12" sm="12" md="12" lg="8" class="d-flex align-center justify-start">
          </v-col>
        </v-row>
      </v-container>

      <!--------------------->
      <!-- Org Information -->
      <!--------------------->
      <v-container fluid class="mt-4 pb-5 pt-4 page-content">
        <v-row class="pa-0 ma-0">
          <v-col cols="12" xs="12" sm="12" md="5" lg="5" class="pt-0">
            <span class="title">Org Details</span>
          </v-col>
          <v-col cols="12" xs="12" sm="12" md="7" lg="7" class="pt-0 d-flex align-center justify-sm-center justify-lg-start">
            <v-btn color="orange darken-3" v-if="canEdit" @click="editItemDetails">
              Edit Details
            </v-btn>
          </v-col>
        </v-row>
        <!-- Joined date -->
        <v-row class="pa-0 ma-0">
          <v-col cols="12" xs="12" sm="12" md="3" lg="2">
            <span class="attr-label">Joined {{ appName }}:</span>
          </v-col>
          <v-col cols="12" xs="12" sm="12" md="9" lg="10">
            <span>{{ formatDateTime(org.created_at) }}</span>
          </v-col>
        </v-row>
      </v-container>

      <!------------------>
      <!-- Org Settings -->
      <!------------------>
      <v-container fluid class="mt-5 pb-5 page-content" v-if="uiType === 'shipper'">
        <!-- Org Settings title -->
        <v-row class="pa-0 ma-0">
          <v-col cols="12" class="pt-0">
            <span class="title">Org Settings</span>
          </v-col>
        </v-row>

        <!-- Confirmation Code section title -->
        <v-row class="pa-0 ma-0 mt-5">
          <v-col cols="12" class="pt-0">
            <span class="section-title">Confirmation Codes</span>
          </v-col>
        </v-row>

        <!-- Confirm code requirement -->
        <v-row class="pa-0 ma-0">
          <v-col cols="12" xs="12" sm="11" md="9" lg="9" class="pb-2">
            <div class="settings-info-text d-flex align-center">
              <div>When enabled, a code must be entered by the driver to pick up an order.</div>
            </div>
          </v-col>
        </v-row>
        <v-row align="center" class="pa-0 ma-0">
          <v-col cols="12" class="d-flex pt-0">
            <v-checkbox
                hide-details
                label="Enable confirmation code for pickups"
                :disabled="!canEdit"
                v-model="isConfirmCodeRequired"
                @change="saveOrgSettings('pickup_confirm_code_required')" />
            <div class="changed-message-text pl-8 pt-5" v-if="orgSettingsSavedMessage">
              <v-icon color="green" class="mr-2 pb-1">mdi-check-bold</v-icon>
              <span>{{ orgSettingsSavedMessage }}</span>
            </div>
          </v-col>
        </v-row>
      </v-container>
    </template>

  </v-container>

</template>

<script>
export default {
  name: 'OrgProfilePage',

  data () {
    return {
      isOrgSettingsSaving: false,
      orgSettingsSavedMessage: null,
      isConfirmCodeRequired: false
    }
  },

  computed: {
    uiType () {
      return this.$root.uiType()
    },
    appName () {
      return this.$store.getters['app/platform']
    },
    orgType () {
      switch (this.uiType) {
        case 'shipper': return 'Shipper Org'
        case 'receiver': return 'Receiver Org'
        case 'sys-admin': return 'Sys Admin Org'
        default: return ''
      }
    },
    org () {
      return this.$store.getters['auth/activeOrg']
    },
    orgToEdit () {
      return this.$store.getters['auth/activeOrgEditing']
    },
    canEdit () {
      return (
        this.$root.activeUserHasRole('sys-admin') ||
        (this.uiType === 'shipper' && this.$root.activeUserHasRole('admin'))
      )
    }
  },

  watch: {
    orgToEdit: {
      // Load org settings
      handler (org) {
        this.isConfirmCodeRequired = (org && this.$root.utils.has(org, 'settings.pickup_confirm_code_required'))
          ? org.settings.pickup_confirm_code_required
          : false
      },
      deep: true
    }
  },

  async beforeMount () {
    // Load active org to edit
    this.$store.dispatch('auth/setActiveOrgToEdit')
  },

  async beforeDestroy () {
    // Clear org editing
    this.$store.dispatch('auth/clearActiveOrgEditing')
  },

  methods: {
    // Display a date/time
    formatDateTime (date) {
      return (date) ? this.$root.utils.format(date, 'YYYY/MM/DD h:mm a') : ''
    },

    // Navigate to edit page
    editItemDetails () {
      this.$root.goTo('org-profile-edit')
    },

    // Save org settings changes
    async saveOrgSettings (propName) {
      this.isOrgSettingsSaving = true

      try {
        if (propName && propName === 'pickup_confirm_code_required') this.orgToEdit.settings[propName] = this.isConfirmCodeRequired

        // Update org
        await this.$store.dispatch('auth/updateOrg')

        // Flash saved message
        this.orgSettingsSavedMessage = 'Changes Saved!'
        setTimeout(() => {
          this.orgSettingsSavedMessage = null
        }, 4000)
      } catch (err) {
        this.orgInfoSaveErrorMessage = err.message || err.text || err
      } finally {
        this.isOrgSettingsSaving = false
      }
    }
  }
}
</script>

<style scoped>
  .page-org-profile .settings-info-text {
    color: #9c9c9c;
  }

  .page-org-profile .changed-message-text {
    font-size: 1.0rem;
    color: #4CAF50;
    font-weight: 400;
  }
</style>
